import { memo, useState } from 'react'

import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { AddCircleOutlineOutlined, SupportAgent } from '@mui/icons-material'
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'

import type { ConfigFormValues } from '../types'
import { PersonPickerDialogContent } from './PersonPickerDialogContent'
import { PersonRow } from './PersonRow'

export type PanelProps = {
  workspaceId: string
  environmentId: string
}

/**
 * On call edited as part of the event form rather than saved per click. The platform replaces the
 * whole list on every patch anyway, so holding the order in form state costs nothing and buys the
 * form's Save, Cancel and Reset — a mistaken removal is undone by walking away, not by re-adding.
 */
const PanelRaw = ({ workspaceId, environmentId }: PanelProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const [pickerOpen, setPickerOpen] = useState(false)

  const { control } = useFormContext<ConfigFormValues>()
  const { fields, append, remove, move } = useFieldArray({ control, name: 'onCall', keyName: 'fieldId' })

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((field) => field.fieldId === active.id)
      const newIndex = fields.findIndex((field) => field.fieldId === over.id)
      if (oldIndex !== -1 && newIndex !== -1) {
        move(oldIndex, newIndex)
      }
    }
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', columnGap: 2 }}>
        <Box>
          <Typography variant="heading-md">{t('domain:Workspace.Event.OnCall.title')}</Typography>
          <Typography sx={{ color: 'text.secondary' }} variant="text-sm">
            {t('domain:Workspace.Event.OnCall.description')}
          </Typography>
        </Box>
        <Button size="small" startIcon={<AddCircleOutlineOutlined />} onClick={() => setPickerOpen(true)}>
          {t('domain:Workspace.Event.OnCall.addPerson')}
        </Button>
      </Box>

      {!fields.length ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 4, color: 'text.secondary' }}>
          <SupportAgent />
          <Typography variant="heading-base">{t('domain:Workspace.Event.OnCall.nobodyOnCall')}</Typography>
        </Stack>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext items={fields.map((field) => field.fieldId)} strategy={verticalListSortingStrategy}>
            <Stack divider={<Divider flexItem />}>
              {fields.map((field, index) => (
                <PersonRow
                  key={field.fieldId}
                  id={field.fieldId}
                  person={field}
                  index={index}
                  onRemove={() => remove(index)}
                />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      )}

      <Dialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t('domain:Workspace.Event.OnCall.selectPersonTitle')}
      >
        <PersonPickerDialogContent
          workspaceId={workspaceId}
          environmentId={environmentId}
          selectedUserIds={fields.map((field) => field.userId)}
          onSelect={(userId, name) => append({ userId, name })}
        />
      </Dialog>
    </Paper>
  )
}

export const Panel = memo(PanelRaw)
